import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { usePlaybooks, useDeletePlaybook, useUpdatePlaybook } from "@/hooks/usePlaybooks";
import { useIsAdmin } from "@/hooks/useAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { getIcon } from "@/lib/iconMap";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Network,
  Shield,
  Loader2 
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function AdminPlaybooks() {
  const navigate = useNavigate();
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { data: playbooks, isLoading } = usePlaybooks(true); // Include unpublished
  const deletePlaybook = useDeletePlaybook();
  const updatePlaybook = useUpdatePlaybook();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  if (adminLoading) {
    return (
      <AdminLayout>
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-8 text-center">
          <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You don't have admin access to this page.</p>
          <Button className="mt-4" onClick={() => navigate("/app/dashboard")}>
            Go to Dashboard
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const handleTogglePublish = async (id: string, currentState: boolean) => {
    try {
      await updatePlaybook.mutateAsync({ id, is_published: !currentState });
      toast.success(currentState ? "Playbook unpublished" : "Playbook published");
    } catch (error) {
      toast.error("Failed to update playbook");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deletePlaybook.mutateAsync(deleteId);
      toast.success("Playbook deleted");
      setDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete playbook");
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Shield className="h-8 w-8 text-primary" />
              Admin: Playbooks
            </h1>
            <p className="text-muted-foreground mt-2">
              Create and manage playbooks for your users
            </p>
          </div>
          <Button onClick={() => navigate("/admin/playbooks/new")}>
            <Plus className="h-4 w-4 mr-2" />
            New Playbook
          </Button>
        </div>

        {/* Playbooks List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : playbooks?.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No playbooks yet</p>
              <Button onClick={() => navigate("/admin/playbooks/new")}>
                Create your first playbook
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {playbooks?.map((playbook) => {
              const Icon = getIcon(playbook.icon_name);
              return (
                <Card key={playbook.id}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground truncate">
                            {playbook.title}
                          </h3>
                          <Badge variant="outline">{playbook.category}</Badge>
                          <Badge variant="secondary">{playbook.tier}</Badge>
                          {!playbook.is_published && (
                            <Badge variant="destructive">Draft</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {playbook.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Published</span>
                          <Switch
                            checked={playbook.is_published}
                            onCheckedChange={() => handleTogglePublish(playbook.id, playbook.is_published)}
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => navigate(`/admin/playbooks/${playbook.id}/nodes`)}
                          title="Edit Decision Tree"
                        >
                          <Network className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => navigate(`/admin/playbooks/${playbook.id}`)}
                          title="Edit Details"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setDeleteId(playbook.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Playbook?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the playbook and all its decision tree nodes.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
